import { useMsal } from '@azure/msal-react';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { logout } from '../Security/azureAdAuthentication';
import { getUserPhoto } from '../GraphApi';

export type UserProfile = {
    username: string; // it contains the email, e.g. name.surname@syensqo.com
    name?: string;
    photo?: string;
};

export type UserContextType = {
    userProfile?: UserProfile;
    isLoadingPhoto: boolean;
    clearUserProfile: () => void;
};

export const UserContext = createContext<UserContextType | undefined>(undefined);

const UserContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [userProfile, setUserProfile] = useState<UserProfile | undefined>(undefined);
    const [isLoadingPhoto, setIsLoadingPhoto] = useState<boolean>(false);
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    
    // If the authenticated user is not from Syensqo (e.g. Solvay), we log them out
    if (activeAccount && !activeAccount.username.endsWith("@syensqo.com")){
        logout();
    }

    useEffect(() => {
        if (activeAccount && !userProfile) {
            setIsLoadingPhoto(true);
            getUserPhoto(instance).then((photo) => {
                setUserProfile({
                    username: activeAccount.username,
                    name: activeAccount.name,
                    photo,
                });
            }).catch((error) => {
                console.error('Error loading photo:', error);
                // Even in case of error, we create the user profile without photo
                setUserProfile({
                    username: activeAccount.username,
                    name: activeAccount.name,
                });
            }).finally(() => {
                setIsLoadingPhoto(false);
            });
        }
    }, [activeAccount, instance, userProfile]);
    const clearUserProfile = () => {
        setUserProfile(undefined);
        setIsLoadingPhoto(false);
    };
    return <UserContext.Provider value={{ clearUserProfile, userProfile, isLoadingPhoto }}>{children}</UserContext.Provider>;
};

export default UserContextProvider;

export const useUserContext = () => useContext(UserContext) as UserContextType;
